import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowRightIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  BanknotesIcon,
  XMarkIcon,
  TrashIcon,
  PlusIcon,
  BuildingOfficeIcon,
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PencilIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { LoadingSpinner, EmptyState, AccessibleButton } from '../../components/ui';
import PaymentDocumentUpload from '../../components/admin/PaymentDocumentUpload';

const PaymentDetailsPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingChecks, setEditingChecks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchPaymentDetails();
    fetchDocuments();
  }, [paymentId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/payments/${paymentId}`);
      setPayment(response.data.payment);
      setAllocations(response.data.allocations || []);
    } catch (error) {
      toast.error('שגיאה בטעינת פרטי התשלום');
      navigate('/admin/payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`/api/admin/payments/${paymentId}/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      const response = await axios.get(`/api/field-agent/payments/${paymentId}/receipt/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${payment?.receipt_number || paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('הקבלה הורדה בהצלחה');
    } catch (error) {
      toast.error('שגיאה בהורדת הקבלה');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את המסמך?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/payments/${paymentId}/documents/${documentId}`);
      toast.success('המסמך נמחק בהצלחה');
      fetchDocuments();
    } catch (error) {
      toast.error('שגיאה במחיקת המסמך');
    }
  };

  const handleDocumentUploaded = () => {
    setShowUploadModal(false);
    fetchDocuments();
    toast.success('המסמך הועלה בהצלחה');
  };

  const handleEditPayment = () => {
    setEditingPayment({
      amount: payment.amount,
      payment_method: payment.payment_method,
      payment_date: payment.payment_date ? new Date(payment.payment_date).toISOString().split('T')[0] : '',
      status: payment.status,
      notes: payment.notes || '',
      bank_number: payment.bank_number || '',
      branch_number: payment.branch_number || ''
    });
    setEditingChecks(payment.checks?.map(check => ({
      id: check.id,
      check_number: check.check_number,
      check_date: check.check_date ? new Date(check.check_date).toISOString().split('T')[0] : '',
      amount: check.amount,
      status: check.status,
      notes: check.notes || ''
    })) || []);
    setShowEditModal(true);
  };

  const handleCancelPayment = async () => {
    if (!cancellationReason.trim()) {
      toast.error('אנא הזן סיבת ביטול');
      return;
    }

    try {
      setCancelling(true);
      const response = await axios.post(`/api/admin/payments/${paymentId}/cancel`, {
        cancellation_reason: cancellationReason.trim()
      });

      toast.success('התשלום בוטל בהצלחה');
      setShowCancelModal(false);
      setCancellationReason('');
      
      // Refresh payment details
      await fetchPaymentDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בביטול התשלום');
    } finally {
      setCancelling(false);
    }
  };

  const handleSavePayment = async () => {
    try {
      setSaving(true);
      await axios.put(`/api/admin/payments/${paymentId}`, {
        ...editingPayment,
        checks: editingChecks
      });
      toast.success('התשלום עודכן בהצלחה');
      setShowEditModal(false);
      fetchPaymentDetails();
    } catch (error) {
      toast.error('שגיאה בעדכון התשלום');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCheckStatus = async (checkId, newStatus) => {
    try {
      await axios.put(`/api/admin/payments/${paymentId}/checks/${checkId}`, {
        status: newStatus
      });
      toast.success('סטטוס הצ\'ק עודכן בהצלחה');
      fetchPaymentDetails();
    } catch (error) {
      toast.error('שגיאה בעדכון הצ\'ק');
    }
  };

  const addNewCheck = () => {
    setEditingChecks([...editingChecks, {
      check_number: '',
      check_date: '',
      amount: 0,
      status: 'pending',
      notes: ''
    }]);
  };

  const removeCheck = (index) => {
    const newChecks = [...editingChecks];
    newChecks.splice(index, 1);
    setEditingChecks(newChecks);
  };

  const updateEditingCheck = (index, field, value) => {
    const newChecks = [...editingChecks];
    newChecks[index][field] = value;
    setEditingChecks(newChecks);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getPaymentMethodLabel = (method, checksCount = 0) => {
    const labels = {
      cash: 'מזומן',
      check: checksCount > 1 ? `צ'קים (${checksCount})` : 'צ\'ק',
      bank_transfer: 'העברה בנקאית'
    };
    return labels[method] || method;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!payment) {
    return (
      <EmptyState
        preset="error"
        title="תשלום לא נמצא"
        description="התשלום המבוקש לא נמצא במערכת"
        action={{
          label: 'חזרה לרשימת התשלומים',
          onClick: () => navigate('/admin/payments')
        }}
      />
    );
  }

  const summary = payment.summary || {
    total_amount: parseFloat(payment.amount),
    allocated_amount: 0,
    unallocated_amount: parseFloat(payment.amount),
    allocated_orders_count: allocations.length
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/payments')}
          className="mb-4 text-primary-600 hover:text-primary-800 flex items-center gap-2"
        >
          <ArrowRightIcon className="h-5 w-5" />
          חזרה לרשימת התשלומים
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">פרטי תשלום</h1>
            <p className="text-gray-600 mt-2">מספר קבלה: {payment.receipt_number}</p>
          </div>
          <div className="flex items-center gap-3">
            {!payment.is_cancelled && payment.status === 'approved' && (
              <AccessibleButton
                variant="danger"
                icon={XMarkIcon}
                onClick={() => setShowCancelModal(true)}
              >
                בטל תשלום
              </AccessibleButton>
            )}
            {!payment.is_cancelled && (
              <AccessibleButton
                variant="secondary"
                icon={PencilIcon}
                onClick={handleEditPayment}
              >
                ערוך תשלום
              </AccessibleButton>
            )}
            <AccessibleButton
              variant="primary"
              icon={DocumentArrowDownIcon}
              onClick={handleDownloadReceipt}
            >
              הורד קבלה
            </AccessibleButton>
          </div>
        </div>
      </div>

      {/* Payment Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">פרטי התשלום</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-gray-500 mb-1">חברה</div>
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
              <span className="text-lg font-medium text-gray-900">
                {payment.company?.name || 'לא זמין'}
              </span>
            </div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">סכום</div>
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="h-5 w-5 text-green-500" />
              <span className="text-lg font-bold text-green-600">
                {formatCurrency(parseFloat(payment.amount))}
              </span>
            </div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">אמצעי תשלום</div>
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-5 w-5 text-gray-400" />
              <span className="text-lg font-medium text-gray-900">
                {getPaymentMethodLabel(payment.payment_method, payment.checks?.length || 0)}
              </span>
            </div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">תאריך תשלום</div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
              <span className="text-lg font-medium text-gray-900">
                {formatDate(payment.payment_date)}
              </span>
            </div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">נגבה על ידי</div>
            <div className="flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-gray-400" />
              <span className="text-lg font-medium text-gray-900">
                {payment.collector ? `${payment.collector.first_name} ${payment.collector.last_name}` : 'לא זמין'}
              </span>
            </div>
          </div>
          
          <div>
            <div className="text-sm text-gray-500 mb-1">סטטוס</div>
            <span className={`px-3 py-1 text-sm rounded-full ${
              payment.status === 'approved' 
                ? 'bg-green-100 text-green-700' 
                : payment.status === 'pending'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {payment.status === 'approved' ? 'אושר' : payment.status === 'pending' ? 'ממתין' : 'נדחה'}
            </span>
          </div>
        </div>

        {payment.notes && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500 mb-1">הערות</div>
            <div className="text-gray-900">{payment.notes}</div>
          </div>
        )}

        {payment.is_cancelled && (
          <div className="mt-4 pt-4 border-t border-red-200 bg-red-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <XMarkIcon className="h-6 w-6 text-red-600 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-red-900 mb-2">תשלום זה בוטל</div>
                {payment.cancellation_reason && (
                  <div className="text-sm text-red-800 mb-2">
                    <span className="font-medium">סיבת ביטול:</span> {payment.cancellation_reason}
                  </div>
                )}
                {payment.cancelled_at && (
                  <div className="text-xs text-red-700">
                    בוטל ב: {formatDate(payment.cancelled_at)} {payment.cancelled_by && `על ידי ${payment.cancelled_by.name || 'משתמש'}`}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Checks Section - Only show if payment method is check */}
      {payment.payment_method === 'check' && payment.checks && payment.checks.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">פרטי צ'קים</h2>
            {(payment.bank_number || payment.branch_number) && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">בנק:</span> {payment.bank_number || '-'} | 
                <span className="font-medium mr-2">סניף:</span> {payment.branch_number || '-'}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מספר צ'ק</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">תאריך</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סכום</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סטטוס</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">הערות</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payment.checks.map((check) => (
                  <tr key={check.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{check.check_number}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(check.check_date)}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-green-600">
                        {formatCurrency(parseFloat(check.amount))}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <select
                        value={check.status}
                        onChange={(e) => handleUpdateCheckStatus(check.id, e.target.value)}
                        className={`px-2 py-1 text-xs rounded-full border-0 cursor-pointer ${
                          check.status === 'cleared' 
                            ? 'bg-green-100 text-green-700'
                            : check.status === 'bounced'
                            ? 'bg-red-100 text-red-700'
                            : check.status === 'cancelled'
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        <option value="pending">ממתין</option>
                        <option value="cleared">פרוע</option>
                        <option value="bounced">חזר</option>
                        <option value="cancelled">מבוטל</option>
                      </select>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-500">{check.notes || '-'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="2" className="px-4 py-3 text-sm font-bold text-gray-700 text-right">
                    סה"כ:
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-green-600">
                    {formatCurrency(payment.checks.reduce((sum, check) => sum + parseFloat(check.amount || 0), 0))}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Allocation Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">סיכום הקצאה</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">סכום כולל</div>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(summary.total_amount)}
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">הוקצה להזמנות</div>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(summary.allocated_amount || 0)}
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">עודף תשלום</div>
            <div className="text-2xl font-bold text-yellow-600">
              {formatCurrency(summary.unallocated_amount || 0)}
            </div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">מספר הזמנות</div>
            <div className="text-2xl font-bold text-purple-600">
              {summary.allocated_orders_count || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Allocated Orders */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">הזמנות ששולמו מתשלום זה</h2>
        
        {allocations.length === 0 ? (
          <EmptyState
            preset="generic"
            title="אין הזמנות שהוקצו"
            description="התשלום לא הוקצה להזמנות עדיין"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מספר הזמנה</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">תאריך הזמנה</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סכום כולל</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סכום ששולם</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">יתרה</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סטטוס</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">פעולות</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allocations.map((allocation) => (
                  <tr key={allocation.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {allocation.order.order_number}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(allocation.order.order_date)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(allocation.order.total_amount)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-green-600">
                        {formatCurrency(allocation.amount_applied)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${
                        allocation.order.remaining_amount > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {formatCurrency(allocation.order.remaining_amount)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        allocation.order.payment_status === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {allocation.order.payment_status === 'paid' ? 'שולם במלואו' : 'שולם חלקית'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Link
                        to={`/admin/orders/${allocation.order.id}`}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        צפה בהזמנה
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Documents Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">מסמכים רשמיים</h2>
          <AccessibleButton
            variant="primary"
            icon={PlusIcon}
            onClick={() => setShowUploadModal(true)}
          >
            העלה מסמך
          </AccessibleButton>
        </div>

        {documents.length === 0 ? (
          <EmptyState
            preset="generic"
            title="אין מסמכים"
            description="לא הועלו מסמכים רשמיים עבור תשלום זה"
          />
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {doc.document_type === 'invoice' ? 'חשבונית' : doc.document_type === 'receipt' ? 'קבלה' : 'מסמך אחר'}
                    </div>
                    <div className="text-xs text-gray-500">{doc.file_name}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      הועלה על ידי: {doc.uploaded_by?.name || 'לא זמין'} • {formatDate(doc.created_at)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                  >
                    צפה
                  </a>
                  <button
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="מחק מסמך"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <PaymentDocumentUpload
          paymentId={paymentId}
          onClose={() => setShowUploadModal(false)}
          onUploaded={handleDocumentUploaded}
        />
      )}

      {/* Edit Payment Modal */}
      {showEditModal && editingPayment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditModal(false)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" dir="rtl">
              <div className="sticky top-0 bg-white px-6 py-4 border-b flex items-center justify-between z-10">
                <h3 className="text-xl font-semibold text-gray-900">עריכת תשלום</h3>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-500">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="px-6 py-4 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">סכום</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingPayment.amount}
                      onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">אמצעי תשלום</label>
                    <select
                      value={editingPayment.payment_method}
                      onChange={(e) => setEditingPayment({ ...editingPayment, payment_method: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="cash">מזומן</option>
                      <option value="check">צ'ק</option>
                      <option value="bank_transfer">העברה בנקאית</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">תאריך תשלום</label>
                    <input
                      type="date"
                      value={editingPayment.payment_date}
                      onChange={(e) => setEditingPayment({ ...editingPayment, payment_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">סטטוס</label>
                    <select
                      value={editingPayment.status}
                      onChange={(e) => setEditingPayment({ ...editingPayment, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="pending">ממתין</option>
                      <option value="approved">אושר</option>
                      <option value="rejected">נדחה</option>
                    </select>
                  </div>
                </div>

                {/* Bank Info (for checks) */}
                {editingPayment.payment_method === 'check' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">מספר בנק</label>
                      <input
                        type="text"
                        value={editingPayment.bank_number}
                        onChange={(e) => setEditingPayment({ ...editingPayment, bank_number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">מספר סניף</label>
                      <input
                        type="text"
                        value={editingPayment.branch_number}
                        onChange={(e) => setEditingPayment({ ...editingPayment, branch_number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">הערות</label>
                  <textarea
                    rows={3}
                    value={editingPayment.notes}
                    onChange={(e) => setEditingPayment({ ...editingPayment, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Checks Section */}
                {editingPayment.payment_method === 'check' && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-gray-900">צ'קים</h4>
                      <button
                        type="button"
                        onClick={addNewCheck}
                        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
                      >
                        <PlusIcon className="h-4 w-4" />
                        הוסף צ'ק
                      </button>
                    </div>

                    {editingChecks.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">אין צ'קים. לחץ "הוסף צ'ק" כדי להוסיף.</p>
                    ) : (
                      <div className="space-y-4">
                        {editingChecks.map((check, index) => (
                          <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-gray-700">צ'ק #{index + 1}</span>
                              <button
                                type="button"
                                onClick={() => removeCheck(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">מספר צ'ק</label>
                                <input
                                  type="text"
                                  value={check.check_number}
                                  onChange={(e) => updateEditingCheck(index, 'check_number', e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">תאריך</label>
                                <input
                                  type="date"
                                  value={check.check_date}
                                  onChange={(e) => updateEditingCheck(index, 'check_date', e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">סכום</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={check.amount}
                                  onChange={(e) => updateEditingCheck(index, 'amount', e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">סטטוס</label>
                                <select
                                  value={check.status}
                                  onChange={(e) => updateEditingCheck(index, 'status', e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                >
                                  <option value="pending">ממתין</option>
                                  <option value="cleared">פרוע</option>
                                  <option value="bounced">חזר</option>
                                  <option value="cancelled">מבוטל</option>
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-xs text-gray-500 mb-1">הערות</label>
                                <input
                                  type="text"
                                  value={check.notes}
                                  onChange={(e) => updateEditingCheck(index, 'notes', e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  ביטול
                </button>
                <AccessibleButton
                  variant="primary"
                  icon={CheckIcon}
                  onClick={handleSavePayment}
                  loading={saving}
                >
                  שמור שינויים
                </AccessibleButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Payment Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">ביטול תשלום</h3>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancellationReason('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  האם אתה בטוח שברצונך לבטל את התשלום <strong>{payment?.receipt_number}</strong>?
                </p>
                <p className="text-sm text-red-600 mb-4">
                  פעולה זו תחזיר את החוב לחברה ותבטל את כל ההקצאות להזמנות.
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סיבת ביטול <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="הזן סיבה לביטול התשלום..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={4}
                  required
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancellationReason('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={cancelling}
                >
                  ביטול
                </button>
                <AccessibleButton
                  variant="danger"
                  icon={XMarkIcon}
                  onClick={handleCancelPayment}
                  loading={cancelling}
                >
                  בטל תשלום
                </AccessibleButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentDetailsPage;
